import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import './SupportChatWidget.css';

function SupportChatWidget({ token, selectedCompany }) {
  const [isOpen, setIsOpen] = useState(false);
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);
  const isLoadingMessagesRef = useRef(false);

  // Мемоизированная функция загрузки сообщений
  const loadMessages = useCallback(async (threadId) => {
    if (!threadId || isLoadingMessagesRef.current) return;

    isLoadingMessagesRef.current = true;
    try {
      const response = await axios.get(
        `/api/v1/support/me/threads/${threadId}/messages`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setMessages(prev => {
        if (JSON.stringify(prev) === JSON.stringify(response.data || [])) {
          return prev;
        }
        return response.data || [];
      });
    } catch (error) {
      console.error('Ошибка загрузки сообщений поддержки:', error);
    } finally {
      isLoadingMessagesRef.current = false;
    }
  }, [token]);

  // Инициализация чата при открытии
  useEffect(() => {
    if (isOpen) {
      initSupportChat();
    }
  }, [isOpen, selectedCompany?.id]);

  // Загрузка сообщений при получении треда
  useEffect(() => {
    if (isOpen && thread?.id) {
      loadMessages(thread.id);
    }
  }, [thread?.id, isOpen, loadMessages]);

  // Автопрокрутка вниз
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Polling сообщений каждые 30 секунд
  useEffect(() => {
    if (!isOpen || !thread?.id) return;

    pollRef.current = setInterval(() => {
      loadMessages(thread.id);
    }, 30000); // 30 секунд

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [isOpen, thread?.id, loadMessages]);

  const initSupportChat = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        '/api/v1/support/me/threads/open',
        {
          tenant_id: selectedCompany?.id || null,
          subject: selectedCompany?.company_name
            ? `Поддержка по компании: ${selectedCompany.company_name}`
            : 'Обращение в техподдержку'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setThread(response.data);
    } catch (error) {
      console.error('Ошибка открытия чата поддержки:', error);
      alert('Не удалось открыть чат поддержки: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    const content = text.trim();
    if (!content || !thread?.id) return;

    setSending(true);
    try {
      await axios.post(
        `/api/v1/support/me/threads/${thread.id}/messages`,
        { content },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setText('');
      await loadMessages(thread.id);
    } catch (error) {
      console.error('Ошибка отправки сообщения в поддержку:', error);
      alert('Не удалось отправить сообщение: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <>
      {!isOpen && (
        <button
          className="support-fab"
          onClick={() => setIsOpen(true)}
          title="Техподдержка"
        >
          ?
        </button>
      )}

      {isOpen && (
        <div className="support-chat-modal">
          <div className="support-chat-window">
            <div className="support-chat-header">
              <div>
                <strong>Техподдержка</strong>
                <div className="support-chat-subtitle">
                  {selectedCompany?.company_name
                    ? `Компания: ${selectedCompany.company_name}`
                    : 'Общий вопрос'}
                </div>
              </div>
              <button
                className="support-chat-close"
                onClick={() => setIsOpen(false)}
              >
                ✕
              </button>
            </div>

            <div className="support-chat-body">
              {loading ? (
                <div className="support-chat-loading">Загрузка чата...</div>
              ) : (
                <div className="support-messages">
                  {messages.length === 0 ? (
                    <div className="support-empty">
                      Напишите в поддержку. Сообщение увидят супер-админы в админке.
                    </div>
                  ) : (
                    messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`support-message ${msg.sender_type === 'user' ? 'from-user' : 'from-admin'}`}
                      >
                        <div className="support-message-sender">
                          {msg.sender_type === 'user' ? 'Вы' : (msg.sender_name || 'Техподдержка')}
                        </div>
                        <div className="support-message-content">{msg.content}</div>
                        <div className="support-message-time">{formatDate(msg.created_at)}</div>
                      </div>
                    ))
                  )}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>

            <div className="support-chat-footer">
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Напишите ваш вопрос..."
                rows={3}
                disabled={sending || loading}
              />
              <button onClick={sendMessage} disabled={sending || loading || !text.trim()}>
                {sending ? 'Отправка...' : 'Отправить'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default SupportChatWidget;